// src/components/auth/SignInButton.jsx
import React from 'react';
import ReactDOM from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import useAuthStore from '../../store/authStore';
import AuthStyle from '../../assets/stylesheets/auth/auth.module.css';
/**
 * Sign In Button Component
 * Shows "Sign In" button when not authenticated
 * Shows user profile menu when authenticated
 */

// Login Modal Component - Renders using Portal
export function LoginModal({ isOpen, onClose, onLogin, onSignupAsUser, onSignupAsFarmer }) {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={AuthStyle["modal-overlay"]} onClick={onClose}>
      <div className={AuthStyle["modal-container"]} onClick={(e) => e.stopPropagation()}>
        <button className={AuthStyle["modal-close"]} onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className={AuthStyle["modal-content"]}>
          <div className={AuthStyle["modal-header"]}>
            <h2 className={AuthStyle["modal-title"]}>Welcome to Farmciti</h2>
            <p className={AuthStyle["modal-subtitle"]}>Sign in to explore amazing farms</p>
          </div>

          <div className={AuthStyle["modal-body"]}>
            <button 
              className={AuthStyle["auth-button-primary"]}
              onClick={onLogin}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Sign In
            </button>

            <div className={AuthStyle["divider"]}>
              <span>or create account</span>
            </div>

            <div className={AuthStyle["role-buttons-container"]}>
              <button 
                className={AuthStyle["auth-button-secondary"]}
                onClick={onSignupAsUser}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div className={AuthStyle["role-button-text"]}>
                  <span className={AuthStyle["role-button-title"]}>Visitor Account</span>
                  {/* <span className={AuthStyle["role-button-description"]}>Explore farms and book experiences</span> */}
                </div>
              </button>

              <button 
                className={AuthStyle["auth-button-secondary"]}
                onClick={onSignupAsFarmer}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <div className={AuthStyle["role-button-text"]}>
                  <span className={AuthStyle["role-button-title"]}>Farmer Account</span>
                  {/* <span className={AuthStyle["role-button-description"]}>List my farm and manage experiences</span> */}
                </div>
              </button>
            </div>
          </div>

          <div className={AuthStyle["modal-footer"]}>
            <p className={AuthStyle["footer-text"]}>
              By continuing, you agree to our{' '}
              <a href="/terms" className={AuthStyle["footer-link"]}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className={AuthStyle["footer-link"]}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function SignInButton({ className = '', style = {}, inSideMenu = false }) {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout } = useAuth0();
  const { role, userProfile, clearAuth } = useAuthStore();
  const navigate = useNavigate(); // Initialize hook
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [buttonPosition, setButtonPosition] = React.useState(null);
  const [hasShownAlert, setHasShownAlert] = React.useState(false);
  const menuRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // Check if user is truly authenticated (with verified email)
  const isFullyAuthenticated = isAuthenticated && user && user.email_verified;

  // Check for unverified email on authentication
  React.useEffect(() => {
    if (isAuthenticated && user && !user.email_verified && !hasShownAlert) {
      setHasShownAlert(true);
      
      // Schedule alert to show after a brief moment (non-blocking)
      setTimeout(() => {
        alert('Please verify your email before signing in. Check your inbox for the verification link.');
      }, 100);
      
      // Immediately clear auth state and logout (doesn't wait for alert)
      clearAuth();
      localStorage.removeItem('ACCESS_TOKEN');
      
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  }, [isAuthenticated, user, hasShownAlert, clearAuth, logout]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (showLoginModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showLoginModal]);

  // Calculate button position for dropdown
  React.useEffect(() => {
    if (isMenuOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right - 15, // Adjust 15px to move menu slightly to the right
      });
    }
  }, [isMenuOpen]);

  const handleSignIn = () => {
    setShowLoginModal(true);
  };

  const handleModalClose = () => {
    setShowLoginModal(false);
  };

  const handleLoginRedirect = () => {
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        screen_hint: 'login',
      },
    });
  };

  const handleSignupAsUser = () => {
    // Save role for fallback and Auth0 Action
    localStorage.setItem('pendingUserRole', 'USER');
    setShowLoginModal(false);
    
    console.log('User selected VISITOR role');
    
    // Redirect to Auth0 signup with role in login_hint
    // Auth0 Action can read this from event.request.query
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        screen_hint: 'signup',
      },
      appState: {
        pendingRole: 'USER',
      },
    });
  };

  const handleSignupAsFarmer = () => {
    // Save role for fallback and Auth0 Action
    localStorage.setItem('pendingUserRole', 'FARMER');
    setShowLoginModal(false);
    
    console.log('User selected FARMER role');
    
    // Redirect to Auth0 signup with role in login_hint
    // Auth0 Action can read this from event.request.query
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        screen_hint: 'signup',
      },
      appState: {
        pendingRole: 'FARMER',
      },
    });
  };

  const handleLogout = () => {
    console.log('Logout button clicked');
    console.log('Current auth state:', { isAuthenticated, user: user?.email });
    
    setIsMenuOpen(false);
    clearAuth();
    localStorage.removeItem('ACCESS_TOKEN');
    
    console.log('Calling Auth0 logout...');
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  if (isLoading) {
    return (
      <button
        className={`${AuthStyle["loading-button"]} ${className}`}
        style={style}
        disabled
      >
        Loading...
      </button>
    );
  }

  // Not authenticated OR email not verified - show Sign In button
  if (!isFullyAuthenticated) {
    return (
      <>
        <button
          onClick={handleSignIn}
          className={`${AuthStyle["signin-button"]} ${className}`}
          style={style}
        >
          Sign In
        </button>

        {/* Login Modal - Rendered at document.body level */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleModalClose}
          onLogin={handleLoginRedirect}
          onSignupAsUser={handleSignupAsUser}
          onSignupAsFarmer={handleSignupAsFarmer}
        />
      </>
    );
  }

  // Authenticated - show user menu
  // In side menu mode: show avatar and menu items directly without hamburger
  if (inSideMenu) {
    return (
      <div className={AuthStyle["side-menu-user-container"]}>
        {/* User Avatar/Initials */}
        <div className={AuthStyle["side-menu-user-header"]}>
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className={AuthStyle["side-menu-user-avatar"]}
            />
          ) : (
            <div className={AuthStyle["side-menu-user-initials"]}>
              {userProfile?.firstName?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
              {userProfile?.lastName?.[0]?.toUpperCase() || user?.name?.split(' ')[1]?.[0]?.toUpperCase() || ''}
            </div>
          )}
        </div>

        {/* Menu Items Directly in Side Menu */}
        <div className={AuthStyle["side-menu-items"]}>
          <button
            onClick={() => {
              navigate('/dashboard/common/profile');
              setIsMenuOpen(false);
            }}
            className={AuthStyle["side-menu-item"]}
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <svg className={AuthStyle["side-menu-item-icon"]} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            My Profile
          </button>
          
          {(role === 'FARMER' || role === 'ADMIN' || role === 'SUPER_ADMIN') && (
            <button
              onClick={() => {
                navigate('/dashboard');
                setIsMenuOpen(false);
              }}
              className={AuthStyle["side-menu-item"]}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <svg className={AuthStyle["side-menu-item-icon"]} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Dashboard
            </button>
          )}
          
          {role === 'USER' && (
            <>
              {/* <button
                onClick={() => {
                  navigate('/bookmarks');
                  setIsMenuOpen(false);
                }}
                className={AuthStyle["side-menu-item"]}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <svg className={AuthStyle["side-menu-item-icon"]} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                My Bookmarks
              </button> */}
              <button
                onClick={() => {
                  navigate('/dashboard/common/favorites');
                  setIsMenuOpen(false);
                }}
                className={AuthStyle["side-menu-item"]}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <svg className={AuthStyle["side-menu-item-icon"]} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.0 0 00-6.364 0z" />
                </svg>
                My Favorites
              </button>
            </>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={AuthStyle["side-menu-logout-button"]}
          >
            <svg className={AuthStyle["side-menu-item-icon"]} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Regular authenticated display - show hamburger menu
  return (
    <>
      <div ref={menuRef} className={AuthStyle["user-menu-container"]}>
        {/* User Avatar - Display Only (Not Clickable) */}
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className={AuthStyle["user-avatar-display"]}
          />
        ) : (
          <div className={AuthStyle["user-avatar-initials"]}>
            {userProfile?.firstName?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
            {userProfile?.lastName?.[0]?.toUpperCase() || user?.name?.split(' ')[1]?.[0]?.toUpperCase() || ''}
          </div>
        )}

        {/* Hamburger Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={`${AuthStyle["hamburger-menu-button"]} ${className}`}
          style={style}
          aria-label="Menu"
        >
          <div className={`${AuthStyle["hamburger-icon"]} ${isMenuOpen ? AuthStyle["open"] : ""}`}>
            <span className={AuthStyle["hamburger-line"]}></span>
            <span className={AuthStyle["hamburger-line"]}></span>
            <span className={AuthStyle["hamburger-line"]}></span>
          </div>
        </button>
      </div>

      {/* Dropdown Menu - Portal to body */}
      {isMenuOpen && buttonPosition && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className={AuthStyle["dropdown-menu-fixed"]}
          style={{
            top: `${buttonPosition.top}px`,
            right: `${buttonPosition.right}px`,
          }}
        >
          {/* User Info Header */}
          <div className={AuthStyle["menu-header"]}>
            <div className={AuthStyle["menu-user-email"]}>
              {user?.email || 'user@example.com'}
            </div>
          </div>
          
          {/* Menu Items */}
          <div className={AuthStyle["menu-items"]}>
            <button
              onClick={() => {
                navigate('/dashboard/common/profile');
                setIsMenuOpen(false);
              }}
              className={AuthStyle["menu-item"]}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <svg className={AuthStyle["menu-item-icon"]} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Profile
            </button>
            
            {(role === 'FARMER' || role === 'ADMIN' || role === 'SUPER_ADMIN') && (
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setIsMenuOpen(false);
                }}
                className={AuthStyle["menu-item"]}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <svg className={AuthStyle["menu-item-icon"]} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Dashboard
              </button>
            )}
            
            {role === 'USER' && (
              <>
                <button
                  onClick={() => {
                    navigate('/dashboard/common/favorites');
                    setIsMenuOpen(false);
                  }}
                  className={AuthStyle["menu-item"]}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <svg className={AuthStyle["menu-item-icon"]} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.0 0 00-6.364 0z" />
                  </svg>
                  My Favorites
                </button>
              </>
            )}
          </div>

          {/* Logout Button */}
          <div className={AuthStyle["menu-footer"]}>
            <button
              onClick={handleLogout}
              className={AuthStyle["logout-button"]}
            >
              <svg className={AuthStyle["menu-item-icon"]} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
